import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Home, ShoppingBag, Briefcase, History, User, LogOut, Inbox, Activity, Bell, Menu, X, MessageCircle, Clock, Coins } from 'lucide-react';
import EquonomyLogo from './EquonomyLogo';

function Layout({ children, user, onLogout }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/marketplace', label: 'Marketplace', icon: ShoppingBag },
    { path: '/my-services', label: 'My Listings', icon: Briefcase },
    { path: '/requests', label: 'Requests', icon: Inbox },
    { path: '/active', label: 'Active', icon: Activity },
    { path: '/messages', label: 'Messages', icon: MessageCircle, badge: user?.unread_messages },
    { path: '/transactions', label: 'History', icon: History },
    { path: '/blockchain', label: 'Blockchain', icon: Coins },
    { path: '/profile', label: 'Profile', icon: User },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }}>
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <div className="flex items-center gap-3">
              <EquonomyLogo className="w-6 h-6 sm:w-8 sm:h-8" gradient={true} />
              <span className="text-xl sm:text-2xl font-bold text-gray-800">Equonomy</span>
            </div>

            {/* Desktop: Balance and User Info */}
            <div className="hidden lg:flex items-center gap-6">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full" data-testid="header-balance">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-900">
                    {user?.time_tokens?.toFixed(1)} tokens
                  </span>
                </div>
                {user?.escrow_tokens > 0 && (
                  <div className="flex items-center gap-2 bg-orange-50 px-4 py-1 rounded-full text-xs" data-testid="header-escrow">
                    <span className="text-orange-600">In Escrow: {user?.escrow_tokens?.toFixed(1)}</span>
                  </div>
                )}
              </div>
              
              <Link to="/notifications" className="relative" data-testid="notification-bell">
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="w-5 h-5" />
                  {user?.unread_notifications > 0 && (
                    <Badge 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-600"
                      data-testid="notification-badge"
                    >
                      {user.unread_notifications}
                    </Badge>
                  )}
                </Button>
              </Link>
              
              <span className="text-gray-700 font-medium" data-testid="header-username">{user?.username}</span>
              <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile: Hamburger Menu */}
            <div className="lg:hidden flex items-center gap-2">
              <Link to="/notifications" className="relative">
                <Button variant="ghost" size="icon">
                  <Bell className="w-5 h-5" />
                  {user?.unread_notifications > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 bg-red-600 text-xs">
                      {user.unread_notifications}
                    </Badge>
                  )}
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>

          {/* Mobile: Expanded User Info */}
          {mobileMenuOpen && (
            <div className="lg:hidden pb-4 space-y-3 border-t pt-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-medium text-sm">{user?.username}</span>
                <div className="flex items-center gap-2 bg-purple-100 px-3 py-1 rounded-full">
                  <Clock className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-900">{user?.time_tokens?.toFixed(1)}</span>
                </div>
              </div>
              {user?.escrow_tokens > 0 && (
                <div className="flex items-center gap-2 bg-orange-50 px-3 py-1 rounded-full text-xs">
                  <span className="text-orange-600">In Escrow: {user?.escrow_tokens?.toFixed(1)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Navigation - Desktop Horizontal */}
      <nav className="hidden lg:block bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors relative ${
                    isActive
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-purple-600 hover:border-purple-300'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{item.label}</span>
                  {item.badge && item.badge > 0 && (
                    <Badge className="ml-1 bg-red-600 text-white text-xs px-1.5 min-w-[20px] h-5">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Navigation - Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={closeMobileMenu}
          />
          
          {/* Menu Panel */}
          <div className="absolute right-0 top-0 bottom-0 w-64 bg-white shadow-xl">
            <div className="p-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold text-gray-800">Menu</h2>
                <Button variant="ghost" size="icon" onClick={closeMobileMenu}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <nav className="space-y-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-purple-100 text-purple-600'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      {item.badge && item.badge > 0 && (
                        <Badge className="bg-red-600 text-white text-xs px-2">
                          {item.badge}
                        </Badge>
                      )}
                    </Link>
                  );
                })}
              </nav>

              <div className="mt-6 pt-6 border-t">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => {
                    closeMobileMenu();
                    onLogout();
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {children}
      </main>
    </div>
  );
}

export default Layout;